import { IWeddingEvent } from '@bridebook/models/source/models/Weddings/Events.types';
import { IGuest, IGuest_Gender, Rsvp } from '@bridebook/models/source/models/Weddings/Guests.types';
import {
  InvitationTypes,
  RsvpStatusesUI,
  OverviewRsvpStatusesUI,
  GuestDietaryOptionsType,
  GuestGender,
  GuestAge,
  ContactsFilter,
} from '@bridebook/web/lib/guestlist/constants';

export interface GuestlistFilterType {
  all: number;
  day: number;
  evening: number;
  waitList: number;
  rsvpAll: number;
  rsvpYes: number;
  rsvpNo: number;
  rsvpNoReply: number;
}

export interface IGuestlistCategory {
  id: string;
  value: string;
}

export interface IGuestlistCategoryWithGuests extends IGuestlistCategory {
  guests: IGuest[];
}

export interface CategoryDetailsPopupState {
  isVisible: boolean;
  category: IGuestlistCategory | null;
}

export type AddGuestsFlowView = 'options' | 'manual' | 'spreadsheet';

export interface IGuestFilter {
  invitationType?: InvitationTypes | null;
  overviewRsvpStatus?: OverviewRsvpStatusesUI | null;
  eventRsvpStatus?: RsvpStatusesUI | null;
  search?: string | null;
  gender?: IGuest_Gender | null;
  age?: Required<IGuest>['age'] | null;
  contacts?: ContactsFilter | null;
  eventId?: string | null;
  dietaryRequirements?: GuestDietaryOptionsType | null;
  table?: string | null;
  overviewRsvpResponse?: RsvpWithMixedResponse | null;
}

/**
 * URL parameters for guestlist page, extending IGuestFilter with tab navigation
 */
export interface IGuestListUrlParams extends Partial<IGuestFilter> {
  tabId?: string;
  edit?: boolean;
}

export interface IGuestlistState {
  list: Array<IGuest>;
  loaded: boolean;
  guest: IGuest;
  edit: boolean;
  groupGuestsToUnlink: Array<string>;
  groupGuestsToLink: Array<string>;
  showSearch: boolean;
  showGuestlistFilters: boolean;
  addressManual: boolean;
  categoryDetailsPopup: CategoryDetailsPopupState;
  addGuestsPopupVisible: boolean;
  addGuestsFlowView: AddGuestsFlowView;
  showImportGuestsModal: boolean;
  showGuestCollectorPopup: boolean;
  isGuestRsvpUpdatePending: boolean;
  guestsMissingEmail: number;
  manageGuestsPopup: {
    eventId: string;
  };
}

export interface ICordovaContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  selected: boolean;
}

export type TGuestlistMobileTab = 'overview' | 'events' | IWeddingEvent['id'];
export type TGuestlistDesktopTab = 'overview' | IWeddingEvent['id'];

/**
 * Reusable RSVP response type that includes the standard Rsvp enum plus mixedResponse
 */
export type RsvpWithMixedResponse = Rsvp | typeof RsvpStatusesUI.mixedResponse;

/**
 * Type guards for nuqs filter validation
 */

export const isGuestGender = (value: string): value is NonNullable<IGuest_Gender> =>
  Object.values(GuestGender).some((gender) => gender === value);

export const isGuestAge = (value: string): value is Required<IGuest>['age'] =>
  Object.values(GuestAge).some((age) => age === value);

export const isTableId = (value: string): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const isOverviewRsvpStatus = (value: string): value is OverviewRsvpStatusesUI =>
  Object.values(OverviewRsvpStatusesUI).some((status) => status === value);

export const isEventRsvpStatus = (value: string): value is RsvpStatusesUI =>
  Object.values(RsvpStatusesUI).some((status) => status === value);

export const isOverviewRsvpResponse = (value: string): value is RsvpWithMixedResponse => {
  const rsvpWithMixedResponse = [...Object.values(Rsvp), RsvpStatusesUI.mixedResponse];
  return rsvpWithMixedResponse.some((rsvp) => rsvp === value);
};

export const isInvitationType = (value: string): value is InvitationTypes =>
  Object.values(InvitationTypes).includes(value as InvitationTypes);

export const isContactsFilter = (value: string): value is ContactsFilter =>
  Object.values(ContactsFilter).includes(value as ContactsFilter);

export const isEventId = (value: string): value is IWeddingEvent['id'] =>
  // UUID v4 pattern (for example: 6da8cf10-0000-4000-8000-000000000000, a16a8484-09cf-466c-ac09-459573ef81a1)
  /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12,13}$/.test(value);

export const isGuestlistFilter = (value: string): value is keyof IGuestFilter => {
  const validKeys = [
    'invitationType',
    'overviewRsvpStatus',
    'eventRsvpStatus',
    'search',
    'gender',
    'age',
    'contacts',
    'eventId',
    'dietaryRequirements',
    'table',
    'overviewRsvpResponse',
  ] as const satisfies ReadonlyArray<keyof IGuestFilter>;

  return validKeys.includes(value as keyof IGuestFilter);
};
